import * as yup from "yup";
import { NAME_VALIDATION_REGEX, NUMBER_VALIDATION_REGEX } from "../../utils/regexExpressions";
import { initializeYup } from "../../utils/yupUtils";
import { IdentifierType } from "@duosoftbg/nacid-frontoffice-components";
import { isEmpty, ReferenceDataCode } from "@duosoftbg/nacid-components";
import i18n from "i18next";

export const createNaturalPersonLibValidationSchema = () => {
  initializeYup(yup);

  const naturalPersonLibValidationSchema = yup
    .object({})
    .concat(createCommonNaturalPersonValidationSchema())
    .concat(createNaturalPersonIdentifierValidationSchema());
  return naturalPersonLibValidationSchema;
};

export const createNaturalPersonFullValidationSchema = () => {
  initializeYup(yup);

  const naturalPersonFullValidationSchema = yup
    .object({
      birthCountry: yup.object({
        id: yup.string().required(),
      }),
      birthPlace: yup
        .string()
        .nullable()
        .max(200, i18n.t("validation.charCount.invalid.200"))
        .test("required", i18n.t("validation.field.select"), function (_value) {
          const _country = this?.parent?.birthCountry.id;
          if (_country !== "BG") {
            return !isEmpty(_value);
          }
          return true;
        }),
      birthSettlement: yup
        .object({
          id: yup.string().max(200, i18n.t("validation.charCount.invalid.200")),
        })
        .nullable()
        .test("required", i18n.t("validation.field.select"), function (_value) {
          const _country = this?.parent?.birthCountry.id;
          if (_country === "BG" && isEmpty(_value?.id)) {
            return this.createError({
              path: this.path + ".id",
              message: i18n.t("validation.field.select"),
            });
          }
          return true;
        }),
      citizenship: yup.object({
        id: yup.string().required(),
      }),
    })
    .concat(createCommonNaturalPersonValidationSchema())
    .concat(createNaturalPersonIdentifierValidationSchema());
  return naturalPersonFullValidationSchema;
};

export const createCommonNaturalPersonValidationSchema = () => {
  initializeYup(yup);

  const naturalPersonCommonValidationSchema = yup.object({
    firstName: yup
      .string()
      .required()
      .max(100, i18n.t("validation.charCount.invalid.100"))
      .matches(NAME_VALIDATION_REGEX, i18n.t("t.validation.field.language.invalid")),
    middleName: yup
      .string()
      .optional()
      .nullable()
      .max(100, i18n.t("validation.charCount.invalid.100"))
      .matches(NAME_VALIDATION_REGEX, i18n.t("t.validation.field.language.invalid"))
      .test("required", i18n.t("validation.field.required"), function (value) {
        const { citizenship, personalIdType } = this.parent;
        if (citizenship && citizenship.id === "BG") {
          return !isEmpty(value);
        }
        if (personalIdType === IdentifierType.NATIONAL_ID) {
          return !isEmpty(value);
        }
        return true;
      }),
    lastName: yup
      .string()
      .nullable()
      .max(100, i18n.t("validation.charCount.invalid.100"))
      .matches(NAME_VALIDATION_REGEX, i18n.t("t.validation.field.language.invalid"))
      .test("required", i18n.t("validation.field.required"), function (value) {
        const { citizenship, personalIdType } = this.parent;
        if (citizenship && citizenship.id === "BG") {
          return !isEmpty(value);
        }
        if (personalIdType === IdentifierType.NATIONAL_ID) {
          return !isEmpty(value);
        }
        return true;
      }),
    dateOfBirth: yup.string().required().max(20, i18n.t("validation.charCount.invalid.20")),
    email: yup.string().optional().email().max(100, i18n.t("validation.charCount.invalid.100")),
    title: yup.string().optional().max(150, i18n.t("validation.charCount.invalid.150")),
  });

  return naturalPersonCommonValidationSchema;
};

export const createNaturalPersonIdentifierValidationSchema = () => {
  initializeYup(yup);

  const naturalPersonIdSchema = yup.object({
    personalIdType: yup.mixed().oneOf(Object.values(IdentifierType)),
    personalId: yup
      .string()
      .max(50, i18n.t("validation.charCount.invalid.50"))
      .test("required", i18n.t("validation.field.required"), function (value) {
        const { personalIdType, foreignerIdentifierKind } = this.parent;
        if (
          personalIdType !== IdentifierType.DOCUMENT_ID ||
          foreignerIdentifierKind?.id !== ReferenceDataCode.FOREIGN_IDENTIFIER_TYPE_NACID_GENERATED
        ) {
          return !isEmpty(value);
        }
        return true;
      }),
    foreignerIdentifierKind: yup.object().when("personalIdType", {
      is: (idType) => idType === IdentifierType.DOCUMENT_ID,
      then: yup.object({
        id: yup.string().required().max(20, i18n.t("validation.charCount.invalid.20")),
      }),
    }),
    foreignerIdentifierCountry: yup.object().when(["personalIdType", "foreignerIdentifierKind"], {
      is: (personalIdType, foreignerIdentifierKind) =>
        personalIdType === IdentifierType.DOCUMENT_ID &&
        foreignerIdentifierKind?.id !== ReferenceDataCode.FOREIGN_IDENTIFIER_TYPE_NACID_GENERATED,
      then: yup.object({
        id: yup.string().required().max(4, i18n.t("validation.charCount.invalid.4")),
      }),
    }),
  });
  return naturalPersonIdSchema;
};

export const createNaturalPersonNamesValidationSchema = () => {
  initializeYup(yup);

  const naturalPersonNamesValidationSchema = yup.object({
    firstName: yup
      .string()
      .required()
      .max(100, i18n.t("validation.charCount.invalid.100"))
      .matches(NAME_VALIDATION_REGEX, i18n.t("t.validation.field.language.invalid")),
    middleName: yup
      .string()
      .optional()
      .nullable()
      .max(100, i18n.t("validation.charCount.invalid.100"))
      .matches(NAME_VALIDATION_REGEX, i18n.t("t.validation.field.language.invalid")),
    lastName: yup
      .string()
      .required()
      .max(100, i18n.t("validation.charCount.invalid.100"))
      .matches(NAME_VALIDATION_REGEX, i18n.t("t.validation.field.language.invalid")),
  });
  return naturalPersonNamesValidationSchema;
};

export const createCompanyValidationSchema = () => {
  initializeYup(yup);

  const companySchema = yup.object({
    companyName: yup.string().required().max(250, i18n.t("validation.charCount.invalid.250")),
    companyIdentifier: yup.string().required().matches(NUMBER_VALIDATION_REGEX),
    companySettlement: yup.object({
      id: yup.string().required().max(200, i18n.t("validation.charCount.invalid.200")),
    }),
  });

  return companySchema;
};

export const createUniversityValidationSchema = () => {
  initializeYup(yup);

  const uniSchema = yup.object({
    universityName: yup.string().required(),
    universityIdentifier: yup.string().required(),
    universitySettlement: yup.object({
      id: yup.string().required(),
    }),
  });

  return uniSchema;
};
